import Expander from "@/components/Expander";
import { TableHead } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { Header } from "@tanstack/react-table";
import DataTableColumnName from "./DataTableColumnName";
import PinColumnButton from "./PinColumnButton";
import SortColumnButton from "./SortColumnButton";

import { useSortable } from "@dnd-kit/sortable";

import { memo, useMemo } from "react";
import { useCellCopySelected } from "../hooks/useCellCopySelected";
import { useDataTableOptions } from "../hooks/useDataTableOptions";
import { useDataTableFeatures } from "../hooks/useDataTableFeatures";

const HEADER_ROW_INDEX = -1;
interface THProps<TData> {
  header: Header<TData, unknown>;
  children?: React.ReactNode;
  className?: string;
  filter?: React.ReactNode;
}

function DataTableHead<TData extends { id: string }>({
  header,
  children,
  filter,
  className,
}: THProps<TData>) {
  const options = useDataTableOptions();
  const { enableColumnOrder } = useDataTableFeatures();
  const isCopySelected = useCellCopySelected(
    HEADER_ROW_INDEX,
    header.column.id,
  );
  const defaultClassName = "relative group flex h-auto px-0 border-r";
  const pinnedHeaderClassName = "sticky left-0 bg-slate-200 z-10";
  const sortedOrFilteredHeaderClassName = "bg-blue-100 text-blue-700";
  const copySelectedHeaderClassName =
    "bg-blue-100 outline outline-blue-300 outline-1";

  const { attributes, listeners, setNodeRef } = useSortable({
    id: header.column.id,
    disabled: header.column.getIsPinned() === "left",
  });

  const isPinned = header.column.getIsPinned();
  const isFilteredOrSorted = options.isFilteredOrSorted(header.column.id);

  const style = useMemo(() => {
    return {
      width: `calc(var(--header-${header.column.id}-size) * 1px)`,
      left: `calc(var(--col-${header.column.id}-position) * 1px)`,
    };
  }, [header.column.id]);

  const columnHandlerProps = useMemo(() => {
    if (enableColumnOrder) {
      return {
        ...listeners,
        ...attributes,
      };
    }
    return {};
  }, [enableColumnOrder, listeners, attributes]);

  const columnHandlerClassName = useMemo(() => {
    if (enableColumnOrder && !isPinned) {
      return "hover:bg-slate-500/10 cursor-grab";
    }
    return "";
  }, [enableColumnOrder, isPinned]);

  return (
    <TableHead
      className={cn(defaultClassName, className, {
        [pinnedHeaderClassName]: isPinned,
        [sortedOrFilteredHeaderClassName]: isFilteredOrSorted,
        [copySelectedHeaderClassName]: isCopySelected,
      })}
      style={style}
      ref={setNodeRef}
      data-column-id={header.column.id}
      data-row-index={HEADER_ROW_INDEX}
    >
      <div className="flex flex-row relative w-full">
        <div className="flex flex-col gap-2 py-2 px-2 w-full">
          <span className="flex items-center gap-1 place-content-between">
            <DataTableColumnName
              header={header}
              {...columnHandlerProps}
              className={columnHandlerClassName}
            />
            <Expander />
            <span className="flex gap-1">
              <SortColumnButton header={header} className="p-0" />
              <PinColumnButton header={header} className="p-0" />
            </span>
          </span>
          {filter}
        </div>
        {children}
      </div>
    </TableHead>
  );
}

export default memo(DataTableHead) as <TData extends { id: string }>(
  props: THProps<TData>,
) => JSX.Element;
